import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ReCAPTCHA from 'react-google-recaptcha';

const SITE_KEY = '6LeZPFQpAAAAAP_2rsIpjB6M8dk0tjLM3li_GcXi';

type Props = {
  open: boolean;
  onVerified: (token: string) => void;
  onClose: () => void;
};

/**
 * Renders via portal on document.body so grecaptcha is not affected by parent
 * overflow/transform. Widget mounts after a short delay so the container exists
 * before Google's script touches the DOM (avoids "Cannot read properties of null (reading 'style')").
 */
export default function RecaptchaModal({ open, onVerified, onClose }: Props) {
  const [widgetKey, setWidgetKey] = useState(0);
  const [widgetReady, setWidgetReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setWidgetReady(false);
      return;
    }
    setWidgetKey((k) => k + 1);
    const t = window.setTimeout(() => setWidgetReady(true), 100);
    return () => {
      window.clearTimeout(t);
      setWidgetReady(false);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  const modal = (
    <div className="people-client-recaptcha-backdrop" role="presentation">
      <div
        className="people-client-recaptcha-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="people-recaptcha-title"
      >
        <h4 id="people-recaptcha-title">Verify your identity</h4>
        <div className="people-client-recaptcha-body">
          {widgetReady ? (
            <ReCAPTCHA
              key={widgetKey}
              sitekey={SITE_KEY}
              size="normal"
              onChange={(token) => {
                if (token) {
                  onVerified(token);
                }
              }}
            />
          ) : null}
        </div>
        <button type="button" className="people-client-recaptcha-close" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
